import posthog from "posthog-js";
import { logger } from "../logger";

/**
 * PostHog analytics wrapper.
 *
 * The whole module is gated on VITE_POSTHOG_KEY: with no key configured it is
 * a complete no-op, so the app runs identically in dev or before analytics is
 * set up. Set VITE_POSTHOG_KEY (and optionally VITE_POSTHOG_HOST) in the
 * environment to switch it on — nothing else needs to change, because
 * telemetry.track() already routes every funnel event through captureEvent().
 */

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST =
  import.meta.env.VITE_POSTHOG_HOST ?? "https://us.i.posthog.com";

let initialized = false;

/** Initialize PostHog once, on the client, only when a key is configured. */
export function initAnalytics(): void {
  if (initialized) return;
  if (typeof window === "undefined") return;
  if (!POSTHOG_KEY) return;

  try {
    // Route ingestion through our own domain (/ingest, proxied to PostHog in
    // vercel.json) so ad blockers see same-origin requests and stop dropping
    // events. ui_host keeps PostHog app links (toolbar, replays) pointing at
    // the real app rather than the proxy.
    posthog.init(POSTHOG_KEY, {
      api_host: `${window.location.origin}/ingest`,
      ui_host: POSTHOG_HOST.replace("i.posthog.com", "posthog.com"),
      capture_pageview: true,
      capture_pageleave: true,
      // Only create person profiles once someone is identified (e.g. at lead
      // capture). Keeps anonymous funnel traffic off the person quota.
      person_profiles: "identified_only",
      autocapture: true,
      // The privacy policy describes exactly what PostHog collects. Pin every
      // feature a PostHog project setting could otherwise switch on remotely
      // (session replay, heatmaps, dead clicks, performance, exceptions,
      // surveys) so collection can't grow without a code and policy change.
      disable_session_recording: true,
      capture_heatmaps: false,
      capture_dead_clicks: false,
      capture_performance: false,
      capture_exceptions: false,
      disable_surveys: true,
    });
    initialized = true;
  } catch (error) {
    logger.error("Failed to initialize PostHog", {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

/** Record a custom event. No-op until initAnalytics() has run with a key. */
export function captureEvent(
  name: string,
  properties?: Record<string, unknown>,
): void {
  if (!initialized) return;
  posthog.capture(name, properties);
}

/**
 * Tie the anonymous funnel journey to a person once we know who they are
 * (called at lead capture). Lets you see, per lead, exactly which assessment
 * steps they completed and whether they booked.
 */
export function identifyUser(
  distinctId: string,
  properties?: Record<string, unknown>,
): void {
  if (!initialized || !distinctId) return;
  posthog.identify(distinctId, properties);
}

export function isAnalyticsInitialized(): boolean {
  return initialized;
}
